import React from 'react';
import { AlertCircle } from 'lucide-react';
import { useMusic } from '../contexts/MusicContext';

/**
 * AdBannerPill — floating overlay shown when the YouTube IFrame slips a
 * pre-roll / mid-roll ad into the queue.
 *
 * The `YouTubeDualDriver` compares the currently-loaded videoId to the
 * queue's expected id every 500 ms; when they diverge for >2 s (past the
 * grace window), it's an ad. The driver has already muted the player;
 * this pill surfaces the state so the operator understands the sudden
 * silence and knows the real track will resume automatically.
 *
 * Positioned bottom-center on desktop, above the mobile bottom-nav on
 * phones. Auto-dismisses when the ad ends (driver fires `onAdEnded`).
 */
export const AdBannerPill = () => {
  const { adPlaying } = useMusic();
  if (!adPlaying) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed left-1/2 -translate-x-1/2 bottom-24 md:bottom-28 z-[60] pointer-events-none animate-fade-in"
      data-testid="ad-banner-pill"
    >
      <div className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-[#0D0D0D]/95 backdrop-blur-md border border-[#FF007F]/40 shadow-xl">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF007F] opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF007F]" />
        </span>
        <AlertCircle className="w-3.5 h-3.5 text-[#FF007F]" />
        <span className="text-white text-xs md:text-sm font-medium font-['Outfit']">
          Skipping YouTube ad — track resumes automatically
        </span>
      </div>
    </div>
  );
};

export default AdBannerPill;
